import { Button, Steps, Step } from '@you/ui'
import { useCallback, useEffect, useState, useMemo } from 'react'
import UploadFile, {
  UploadFile as UploadFileType,
} from '@/components/common/UploadFile'
import FileCard from '@/components/common/FileCard'
import DatasetSettingTable, {
  TableColumn,
} from '@/components/Knowledge/DatasetSettingTable'
import DatasetPreviewTable from '@/components/Knowledge/DatasetPreviewTable'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { KnowledgeDocType } from '@/api/types/knowledge.types'
import { useDatasetFiles } from '@/api/hooks/useDatasetFiles'
import { datasetFilesService } from '@/api/services/datasetFiles.service'
import { SheetInfo } from '@/api/types/datasetFiles.type'

type ProcessStatus = 'waiting' | 'processing' | 'completed'

const SUPPORTED_FILE_TYPES = ['.xlsx']

interface TableDatasetImportFlowProps {
  onFinish: () => void
}

const TableDatasetImportFlow = ({ onFinish }: TableDatasetImportFlowProps) => {
  const { currentKnowledge, currentDataset } = useKnowledgeStore()
  const { currentWorkspace } = useWorkspaceStore()

  const [uploadFiles, setUploadFiles] = useState<UploadFileType[]>([])
  const [currentStep, setCurrentStep] = useState(0)

  // 表格设置状态
  const [tableSettings, setTableSettings] = useState({
    dataTable: '',
    headerRow: '1',
    dataStartRow: '2',
    columns: [
      {
        id: '1',
        isIndex: false,
        columnName: '',
        description: '',
        dataType: '',
      },
    ] as TableColumn[],
  })

  const [sheetInfo, setSheetInfo] = useState<SheetInfo>()

  // 获取数据集文件操作方法
  const {
    refresh: refreshFileList,
    uploadFiles: uploadDatasetFiles,
    removeFile: deleteDatasetFile,
  } = useDatasetFiles({
    datasetId: currentDataset?.id || '',
  })

  // 正在上传的文件列表
  const [uploadingFiles, setUploadingFiles] = useState<
    {
      id: string
      name: string
      size: number
      status: 'uploading' | 'error'
      file: File
      error?: string
    }[]
  >([])

  // 文件处理状态
  const [fileProcessStatus, setFileProcessStatus] = useState<
    Record<string, ProcessStatus>
  >({})
  const [isProcessing, setIsProcessing] = useState(false)
  const [isParseCompleted, setIsParseCompleted] = useState(false)

  // 使用 useMemo 优化 DatasetPreviewTable 的参数，避免不必要的重新渲染
  const datasetPreviewParams = useMemo(() => {
    if (currentStep === 3 && uploadFiles.length > 0) {
      return {
        datasetFileId: uploadFiles[0].serverId || uploadFiles[0].id,
        datasetId: currentDataset?.id || '',
        knowledgeBaseId: currentKnowledge?.id || '',
        workspaceId: currentWorkspace?.id || '',
      }
    }
    return null
  }, [
    currentStep,
    uploadFiles,
    currentDataset?.id,
    currentKnowledge?.id,
    currentWorkspace?.id,
  ])

  const handleFileUpload = useCallback(
    async (file: UploadFileType) => {
      const workspaceId = currentWorkspace?.id
      if (!workspaceId || !currentDataset?.id) return false

      const uploadingFile = {
        id: file.id,
        name: file.file.name,
        size: file.file.size,
        status: 'uploading' as const,
        file: file.file,
      }
      setUploadingFiles((prev) => [uploadingFile, ...prev])

      try {
        const uploadParams = {
          datasetId: currentDataset.id,
          knowledgeBaseId: currentKnowledge?.id || '',
          workspaceId,
          file: file.file,
        }

        const result = await uploadDatasetFiles(uploadParams)
        setUploadingFiles((prev) => prev.filter((f) => f.id !== file.id))
        return result
      } catch (error) {
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.id === file.id
              ? { ...f, status: 'error', error: '上传失败，请重试' }
              : f
          )
        )
        return false
      }
    },
    [
      currentDataset?.id,
      currentKnowledge?.id,
      currentWorkspace?.id,
      uploadDatasetFiles,
    ]
  )

  const handleRetryUpload = useCallback(
    async (fileId: string) => {
      const fileToRetry = uploadingFiles.find((f) => f.id === fileId)
      if (!fileToRetry) return
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, status: 'uploading', error: undefined } : f
        )
      )
      await handleFileUpload({
        id: fileId,
        file: fileToRetry.file,
        status: 'WAITING',
        progress: 0,
      })
    },
    [uploadingFiles, handleFileUpload]
  )

  const handleDeleteUploadingFile = useCallback((fileId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId))
  }, [])

  // 获取表格设置
  const fetchTableSetting = useCallback(
    async (files: UploadFileType[]) => {
      if (
        currentKnowledge?.docType === KnowledgeDocType.TABLE &&
        currentDataset?.id &&
        currentKnowledge?.id &&
        currentWorkspace?.id &&
        files.length > 0 // 有文件时才调用
      ) {
        try {
          const response = await datasetFilesService.getTableSetting({
            datasetId: parseInt(currentDataset.id),
            knowledgeBaseId: parseInt(currentKnowledge.id),
            workspaceId: parseInt(currentWorkspace.id),
            datasetFileIds: files.map((file) =>
              parseInt(file.serverId || file.id)
            ),
          })

          const firstSheet = response?.sheetInfoList?.[0]
          if (firstSheet && firstSheet.rowDataList?.[0]?.cellData) {
            setSheetInfo(firstSheet)

            // 设置默认的数据表和列信息
            const headerRowData = firstSheet.rowDataList[0].cellData
            const newColumns = Object.entries(headerRowData).map(
              ([, value], index) => ({
                id: index.toString(),
                isIndex: false,
                columnName: String(value),
                description: '',
                dataType: '',
              })
            )

            setTableSettings((prev) => ({
              ...prev,
              dataTable: firstSheet.sheetName,
              columns: newColumns,
            }))
          }
        } catch (error) {
          console.error('获取表格设置失败:', error)
        }
      }
    },
    [
      currentKnowledge?.docType,
      currentKnowledge?.id,
      currentDataset?.id,
      currentWorkspace?.id,
    ]
  )

  const handleFilesChange = useCallback(
    async (files: UploadFileType[]) => {
      // 更新本地上传文件列表
      setUploadFiles(files)

      const newFiles = files.filter(
        (file) => !uploadingFiles.some((uf) => uf.id === file.id)
      )
      if (newFiles.length === 0) return

      // 批量上传文件
      const workspaceId = currentWorkspace?.id
      if (!workspaceId || !currentDataset?.id) return

      try {
        const uploadParams = {
          datasetId: currentDataset.id,
          knowledgeBaseId: currentKnowledge?.id || '',
          workspaceId,
          file: newFiles.map((file) => file.file), // 使用 file 字段，支持数组
        }

        const result = await uploadDatasetFiles(uploadParams)

        // 如果上传成功，为本地文件分配服务器返回的ID
        if (
          result &&
          Array.isArray(result) &&
          result.length === newFiles.length
        ) {
          const updatedFiles = files.map((file) => {
            // 如果是新上传的文件，使用服务器返回的ID
            const newFileIndex = newFiles.findIndex(
              (newFile) => newFile.id === file.id
            )
            if (newFileIndex !== -1 && result[newFileIndex]) {
              return {
                ...file,
                serverId: result[newFileIndex], // 添加服务器ID
              }
            }
            return file
          })
          setUploadFiles(updatedFiles)
          console.log('uploadFiles', updatedFiles)

          // 上传成功后，获取表格设置
          if (currentKnowledge?.docType === KnowledgeDocType.TABLE) {
            await fetchTableSetting(updatedFiles)
          }
        }
      } catch (error) {
        console.error('批量上传失败:', error)
      }
    },
    [
      currentDataset?.id,
      currentKnowledge?.id,
      currentWorkspace?.id,
      uploadDatasetFiles,
      uploadingFiles,
      fetchTableSetting,
    ]
  )

  const renderFileList = () => {
    return (
      <div className="space-y-3">
        {uploadingFiles.map((file) => (
          <FileCard
            key={file.id}
            file={{
              id: file.id,
              name: file.name,
              size: file.size,
              status: file.status === 'uploading' ? 'UPLOADING' : 'ERROR',
              progress: file.status === 'uploading' ? 50 : 0,
              error: file.error,
              file: file.file,
            }}
            type="upload"
            showDelete={true}
            onDelete={handleDeleteUploadingFile}
            onRetry={handleRetryUpload}
          />
        ))}

        {uploadFiles.map((file: UploadFileType) => (
          <FileCard
            key={file.id}
            file={{
              id: file.id,
              name: file.file.name,
              size: file.file.size,
              status: 'SUCCESS',
              progress: 100,
            }}
            type="upload"
            showDelete={true}
            onDelete={async (fileId) => {
              // 从本地状态中移除
              setUploadFiles((prev) => prev.filter((f) => f.id !== fileId))

              // 如果文件已经上传成功（有 serverId），则调用删除接口
              const fileToDelete = uploadFiles.find((f) => f.id === fileId)
              if (fileToDelete?.serverId) {
                try {
                  await deleteDatasetFile(fileToDelete.serverId)
                  await refreshFileList()
                } catch (error) {
                  console.error('删除服务器文件失败:', error)
                }
              }
            }}
          />
        ))}
      </div>
    )
  }

  const handleSettingSave = useCallback(async () => {
    const workspaceId = currentWorkspace?.id || ''
    if (!workspaceId) return

    const { dataTable, headerRow, dataStartRow, columns } = tableSettings

    // 转换列信息为接口需要的格式
    const tableColumnsList = columns.map((col, index) => ({
      cellIndex: index,
      columnDesc: col.description,
      columnName: col.columnName,
      columnType: col.dataType,
      isIndex: col.isIndex ? 1 : 0,
    }))

    await datasetFilesService.saveTableSetting({
      datasetFileId: parseInt(uploadFiles[0].serverId || uploadFiles[0].id), // 使用服务器ID，如果没有则使用本地ID
      datasetId: parseInt(currentDataset?.id || '0'),
      dataStartRows: parseInt(dataStartRow),
      headerRows: parseInt(headerRow),
      knowledgeBaseId: parseInt(currentKnowledge?.id || '0'),
      tableColumnsList,
      tableName: dataTable,
      workspaceId: parseInt(workspaceId),
    })

    await refreshFileList()
    setCurrentStep(2)
  }, [
    uploadFiles,
    currentWorkspace?.id,
    currentDataset?.id,
    currentKnowledge?.id,
    tableSettings,
    refreshFileList,
  ])

  const handleParseFiles = useCallback(async () => {
    if (
      !currentDataset?.id ||
      !currentKnowledge?.id ||
      !currentWorkspace?.id ||
      uploadFiles.length === 0
    ) {
      return
    }

    setIsProcessing(true)
    const newStatus = uploadFiles.reduce((acc, file) => {
      acc[file.id] = 'processing'
      return acc
    }, {} as Record<string, ProcessStatus>)
    setFileProcessStatus(newStatus)

    try {
      // 调用解析表格接口
      await datasetFilesService.parseTable({
        datasetFileId: parseInt(uploadFiles[0].serverId || uploadFiles[0].id), // 使用服务器ID，如果没有则使用本地ID
        datasetId: parseInt(currentDataset.id),
        knowledgeBaseId: parseInt(currentKnowledge.id),
        workspaceId: parseInt(currentWorkspace.id),
      })

      // 模拟解析进度
      for (let i = 0; i < uploadFiles.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 200))
        setFileProcessStatus((prev) => ({
          ...prev,
          [uploadFiles[i].id]: 'completed',
        }))
      }

      setIsParseCompleted(true)
    } catch (error) {
      console.error('解析表格失败:', error)
      const waitingStatus = uploadFiles.reduce((acc, file) => {
        acc[file.id] = 'waiting'
        return acc
      }, {} as Record<string, ProcessStatus>)
      setFileProcessStatus(waitingStatus)
    } finally {
      setIsProcessing(false)
    }
  }, [
    currentDataset?.id,
    currentKnowledge?.id,
    currentWorkspace?.id,
    uploadFiles,
  ])

  const initializeFileStatus = useCallback(() => {
    if (uploadFiles.length === 0) return
    const initialStatus = uploadFiles.reduce((acc, file) => {
      acc[file.id] = 'waiting'
      return acc
    }, {} as Record<string, ProcessStatus>)
    setFileProcessStatus(initialStatus)
  }, [uploadFiles])

  useEffect(() => {
    initializeFileStatus()
  }, [uploadFiles.length])

  useEffect(() => {
    if (currentStep === 2) {
      setIsProcessing(false)
      setIsParseCompleted(false)
      initializeFileStatus()
    }
  }, [currentStep, initializeFileStatus])

  const canGoNext = () => {
    switch (currentStep) {
      case 0:
        return allUploaded
      case 1:
        // 表格类型的验证：检查必填项
        const { dataTable, headerRow, dataStartRow, columns } = tableSettings
        const hasValidColumns =
          columns.length > 0 &&
          columns.every((col) => col.columnName.trim() && col.dataType)
        return dataTable.trim() && headerRow && dataStartRow && hasValidColumns
      case 2:
        return isParseCompleted
      default:
        return true
    }
  }

  const handleNext = useCallback(async () => {
    if (currentStep < 3) {
      if (currentStep === 0) {
        setCurrentStep(currentStep + 1)
        return
      }
      if (currentStep === 1) {
        try {
          await handleSettingSave()
          return
        } catch {
          return
        }
      } else if (currentStep === 2) {
        setCurrentStep(currentStep + 1)
      }
    }
  }, [currentStep, handleSettingSave])

  const handlePrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep(currentStep - 1)
  }, [currentStep])

  const allUploaded = useMemo(() => {
    const hasUploadingFiles = uploadingFiles.length > 0
    const hasUploadedFiles = uploadFiles.length > 0
    return !hasUploadingFiles && hasUploadedFiles
  }, [uploadingFiles, uploadFiles])

  return (
    <div className="flex-1 w-full flex flex-col min-h-0 overflow-auto">
      <div className="max-w-[1200px] mx-auto w-full">
        <div className="mb-4 px-8">
          <Steps
            active={currentStep}
            direction="horizontal"
            finishStatus="success"
          >
            <Step title="上传" />
            <Step title="设置" />
            <Step title="数据处理" />
            <Step title="表格预览" />
          </Steps>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-auto w-full mb-3">
        {currentStep === 0 && (
          <div className="max-w-[1200px] mx-auto w-full">
            <UploadFile
              acceptedTypes={SUPPORTED_FILE_TYPES}
              maxFiles={1}
              maxSizeMB={100}
              multiple={false}
              files={uploadFiles}
              onFilesChange={handleFilesChange}
              showFileList={false}
              fileListClassName="flex-1 overflow-y-auto mb-8"
            />
            {renderFileList()}
          </div>
        )}

        {currentStep === 1 && (
          <div className="max-w-[1200px] mx-auto w-full">
            <DatasetSettingTable
              dataTable={tableSettings.dataTable}
              headerRow={tableSettings.headerRow}
              dataStartRow={tableSettings.dataStartRow}
              columns={tableSettings.columns}
              onDataTableChange={(value) =>
                setTableSettings((prev) => ({ ...prev, dataTable: value }))
              }
              onHeaderRowChange={(value) =>
                setTableSettings((prev) => ({ ...prev, headerRow: value }))
              }
              onDataStartRowChange={(value) =>
                setTableSettings((prev) => ({ ...prev, dataStartRow: value }))
              }
              onColumnsChange={(columns) =>
                setTableSettings((prev) => ({ ...prev, columns }))
              }
              sheetInfo={sheetInfo}
            />
          </div>
        )}

        {currentStep === 2 && (
          <div className="max-w-[1200px] mx-auto w-full">
            <div className="mb-4">
              <h3 className="text-base font-bold text-[#333333] mb-2">
                数据处理进度
              </h3>
            </div>
            <div className="space-y-3">
              {uploadFiles.map((file: UploadFileType) => {
                const status = fileProcessStatus[file.id] || 'waiting'
                return (
                  <FileCard
                    key={file.id}
                    file={{
                      id: file.id,
                      name: file.file.name,
                      size: file.file.size,
                      status:
                        status === 'waiting'
                          ? 'WAITING'
                          : status === 'processing'
                          ? 'processing'
                          : 'completed',
                      progress:
                        status === 'completed'
                          ? 100
                          : status === 'processing'
                          ? 50
                          : 0,
                    }}
                    type="process"
                    showDelete={false}
                  />
                )
              })}
            </div>
          </div>
        )}

        {datasetPreviewParams && (
          <DatasetPreviewTable
            datasetFileId={datasetPreviewParams.datasetFileId}
            datasetId={datasetPreviewParams.datasetId}
            knowledgeBaseId={datasetPreviewParams.knowledgeBaseId}
            workspaceId={datasetPreviewParams.workspaceId}
          />
        )}
      </div>

      <div className="flex justify-center space-x-4 flex-none">
        {currentStep > 0 && (
          <Button onClick={handlePrev} variant="outline" className="w-[160px]">
            上一步
          </Button>
        )}
        {currentStep === 2 ? (
          <div className="flex justify-center space-x-4">
            {(!isParseCompleted || isProcessing) && (
              <div
                className={`transition-all duration-500 ease-in-out transform ${
                  isParseCompleted
                    ? 'opacity-0 scale-95'
                    : 'opacity-100 scale-100'
                }`}
              >
                <Button
                  onClick={handleParseFiles}
                  disabled={isProcessing}
                  className="bg-gradient-primary w-[160px]"
                >
                  {isProcessing ? '解析中...' : '解析文件'}
                </Button>
              </div>
            )}
            {isParseCompleted && (
              <div
                className={`transition-all duration-500 ease-in-out transform ${
                  isParseCompleted
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-full'
                }`}
              >
                <Button
                  onClick={() => setCurrentStep(3)}
                  className="bg-gradient-primary w-[160px]"
                >
                  下一步
                </Button>
              </div>
            )}
          </div>
        ) : currentStep < 3 ? (
          <Button
            onClick={handleNext}
            disabled={!canGoNext()}
            className="bg-gradient-primary w-[160px]"
          >
            {currentStep === 1 ? '保存设置' : '下一步'}
          </Button>
        ) : (
          <Button onClick={onFinish} className="bg-gradient-primary w-[160px]">
            完成
          </Button>
        )}
      </div>
    </div>
  )
}

export default TableDatasetImportFlow
